import React, { useState, useEffect } from "react";
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Link } from "react-router-dom";
import { STORE_READY_KEY, ONBOARDING_TO_PRODUCTS_KEY } from "./StoreReady";
import { getPendingPlan, purchasePlanPath } from "../utils/pendingPlan";
import { loginLandingPath } from "../utils/loginLanding";
import { saveSellerToken } from "../utils/sellerSession";
import { rateLimitMessage } from "../utils/rateLimit";
import { typedStoreName } from "../utils/storeSubdomain";
import AuthLayout from "./auth/AuthLayout";
import OtpBoxes from "./auth/OtpBoxes";
import PlanSummary, { usePendingPlan } from "./auth/PlanSummary";

const OTP_LENGTH = 4;
const RESEND_SECONDS = 60;

// An Indian mobile number: 10 digits, starting 6-9
export const isMobileNumber = (value) => /^[6-9]\d{9}$/.test(value);
// "98765 43210"
export const spacedPhone = (value) => `${value.slice(0, 5)} ${value.slice(5)}`;

// Public IP for the signup record, from the first service that answers
const fetchIPAddress = async () => {
    const ipServices = [
        { url: 'https://api.ipify.org?format=json', key: 'ip' },
        { url: 'https://api.ip.sb/ip', key: null, isText: true },
        { url: 'https://ipapi.co/json/', key: 'ip' },
        { url: 'https://api.bigdatacloud.net/data/client-ip', key: 'ipString' }
    ];

    for (const service of ipServices) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            const response = await fetch(service.url, { signal: controller.signal, headers: { 'Accept': 'application/json' } });
            clearTimeout(timeoutId);
            if (!response.ok) continue;

            if (service.isText) return (await response.text()).trim();
            const data = await response.json();
            const ip = service.key ? data[service.key] : data.ip;
            if (ip) return ip;
        } catch (error) {
            console.warn(`Failed to fetch IP from ${service.url}:`, error.message);
        }
    }
    console.error("All IP detection services failed");
    return "unavailable";
};

const RegistrationForm = () => {
    const { t } = useTranslation();
    // The plan picked on mytamilkart.com, shown beside the form
    const pickedPlan = usePendingPlan();

    const [ipAddress, setIpAddress] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [terms, setTerms] = useState(false);
    const [createWebStore, setCreateWebStore] = useState(true);
    const [customStoreUrl, setCustomStoreUrl] = useState("");
    // "radha-" is fine while typing, but not as a store URL: https://radha-.mytamilkart.com doesn't open
    const storeNameEndsWithHyphen = customStoreUrl.endsWith("-");
    const [urlAvailability, setUrlAvailability] = useState({ status: null });
    const [checkingUrl, setCheckingUrl] = useState(false);
    const [urlSuggestions, setUrlSuggestions] = useState([]);
    const [formError, setFormError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showOtpSection, setShowOtpSection] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpError, setOtpError] = useState("");
    const [resendTimer, setResendTimer] = useState(0);
    // The number already has a verified account: the OTP signs in to it instead
    const [existingAccount, setExistingAccount] = useState(false);
    // Whether the typed number already has an account (/business/check-phone):
    // "idle" | "checking" | "new" | "registered" | "error" | "limited" (the API's
    // 429). A store URL can only be typed for a new number: a registered one
    // signs in to its own store.
    const [phoneStatus, setPhoneStatus] = useState("idle");
    const [phoneCheckTry, setPhoneCheckTry] = useState(0);
    const storeUrlLocked = phoneStatus !== "new";
    const phoneCheckFailed = phoneStatus === "error" || phoneStatus === "limited";

    useEffect(() => {
        fetchIPAddress().then(setIpAddress).catch(() => setIpAddress("unavailable"));
    }, []);

    useEffect(() => {
        if (resendTimer <= 0) return undefined;
        const timer = setTimeout(() => setResendTimer(s => s - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendTimer]);

    // Check the number once all 10 digits are in. A reply for a number the seller
    // has since changed is ignored.
    useEffect(() => {
        if (!isMobileNumber(phoneNumber)) {
            setPhoneStatus("idle");
            return undefined;
        }
        let current = true;
        setPhoneStatus("checking");
        fetch(`${process.env.REACT_APP_APIURL}business/check-phone`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ phone_number: phoneNumber }),
        })
            // A 429 means wait, not "Try again" straight away
            .then(response => (response.status === 429 ? { limited: true } : response.json()))
            .then(data => {
                if (!current) return;
                if (data.limited) {
                    setPhoneStatus("limited");
                } else if (data.status !== "success" || typeof data.result?.registered !== "boolean") {
                    setPhoneStatus("error");
                } else {
                    setPhoneStatus(data.result.registered ? "registered" : "new");
                }
            })
            .catch(error => {
                console.error("Error checking phone number:", error);
                if (current) setPhoneStatus("error");
            });
        return () => { current = false; };
    }, [phoneNumber, phoneCheckTry]);

    // A registered number keeps its own store: drop a store name typed for another number
    useEffect(() => {
        if (phoneStatus !== "registered") return;
        setCustomStoreUrl("");
        setUrlAvailability({ status: null });
        setUrlSuggestions([]);
    }, [phoneStatus]);

    // Check the store name half a second after typing stops
    useEffect(() => {
        if (!createWebStore || storeUrlLocked || customStoreUrl.length < 3 || storeNameEndsWithHyphen) {
            setUrlAvailability({ status: null });
            setUrlSuggestions([]);
            return undefined;
        }
        const timer = setTimeout(() => checkUrlAvailability(customStoreUrl), 500);
        return () => clearTimeout(timer);
    }, [customStoreUrl, storeNameEndsWithHyphen, createWebStore, storeUrlLocked]);

    const checkUrlAvailability = async (urlName) => {
        setCheckingUrl(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_APIURL}business/store/check-subdomain`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ store_subdomain: urlName, get_suggestions: true }),
            });
            const data = await response.json();

            if (data.status === "success") {
                setUrlAvailability({ status: "available" });
                setUrlSuggestions([]);
            } else if (data.status === "conflict") {
                setUrlAvailability({ status: "unavailable" });
                setUrlSuggestions(data.result?.details?.suggestions || []);
            } else {
                setUrlAvailability({ status: "error" });
                setUrlSuggestions([]);
            }
        } catch (error) {
            console.error("Error checking URL availability:", error);
            setUrlAvailability({ status: "error" });
            setUrlSuggestions([]);
        } finally {
            setCheckingUrl(false);
        }
    };

    // The first thing stopping the form, or ""
    const firstError = () => {
        if (!phoneNumber.trim()) return t("messages.phone_required");
        if (phoneNumber.length !== 10) return t("messages.phone_length");
        if (!isMobileNumber(phoneNumber)) return t("auth.phone_bad");
        if (phoneStatus === "checking") return t("auth.phone_checking");
        if (phoneStatus === "error") return t("auth.phone_check_failed");
        if (phoneStatus === "limited") return t("auth.too_many");
        // Signs in: no store name, and the terms were accepted at sign-up
        if (phoneStatus === "registered") return "";
        if (createWebStore) {
            if (!customStoreUrl) return t("messages.custom_url_required");
            if (customStoreUrl.length < 3) return t("messages.custom_url_min");
            if (storeNameEndsWithHyphen) return t("messages.custom_url_hyphen_end");
            if (urlAvailability.status === "unavailable") return t("messages.custom_url_unavailable");
        }
        if (!terms) return t("messages.terms_required");
        return "";
    };

    const signupPayload = () => ({
        phone_number: phoneNumber,
        terms_accepted: String(terms),
        ip_address: ipAddress,
        create_web_store: createWebStore,
        custom_store_url: createWebStore ? customStoreUrl : "",
    });

    // Sends the OTP; again for a "Resend", which issues a new code
    const requestOtp = () => axios.post(`${process.env.REACT_APP_APIURL}business/signups`, signupPayload(), {
        headers: { "Content-Type": "application/json" },
    });
    // The login OTP, for a number that already has an account
    const requestLoginOtp = () => axios.post(`${process.env.REACT_APP_APIURL}business/otplogin`, { phone_number: phoneNumber });

    const showOtpBoxes = () => {
        setOtp("");
        setOtpError("");
        setShowOtpSection(true);
        setResendTimer(RESEND_SECONDS);
    };

    // /business/signups' 409 (status "conflict") for a number that already signed up
    // and verified (uniquegiftapi app/business.js): sign in to that account
    // with an OTP, so a seller who picked a plan on mytamilkart.com still gets to
    // /purchase-plan (which offers the switch from their current plan)
    const signInExistingAccount = async () => {
        const { data } = await requestLoginOtp();
        if (data.status !== "success") {
            toast.error(data.message || "This mobile already exists, please sign in.");
            return;
        }
        setExistingAccount(true);
        showOtpBoxes();
        toast.info(t("auth.existing_account", { phone: phoneNumber }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const error = firstError();
        setFormError(error);
        if (error) return;

        setIsSubmitting(true);
        if (phoneStatus === "registered") {
            try {
                await signInExistingAccount();
            } catch (loginError) {
                console.error("Error sending login OTP:", loginError);
                toast.error(rateLimitMessage(loginError, t("auth.too_many")) || loginError.response?.data?.message || "Something went wrong. Please try again later.");
            } finally {
                setIsSubmitting(false);
            }
            return;
        }
        try {
            const { data } = await requestOtp();
            if (data.status === "success") {
                // A new number: the OTP creates this account (not a login to an earlier one)
                setExistingAccount(false);
                showOtpBoxes();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            if (error.response?.status === 409 && error.response.data?.status === "conflict") {
                try {
                    await signInExistingAccount();
                } catch (loginError) {
                    console.error("Error sending login OTP:", loginError);
                    toast.error(rateLimitMessage(loginError, t("auth.too_many")) || error.response.data?.message);
                }
                return;
            }
            console.error("Error during submission:", error);
            toast.error(rateLimitMessage(error, t("auth.too_many")) || error.response?.data?.message || "Something went wrong. Please try again later.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const verifyOTP = async (code) => {
        if (code.length !== OTP_LENGTH) {
            setOtpError(t("auth.otp_incomplete"));
            return;
        }
        setOtpError("");
        setIsSubmitting(true);

        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_APIURL}business/verifyOtp`,
                { phone_number: phoneNumber, otp: code },
                { headers: { "Content-Type": "application/json" } }
            );
            if (data.status !== "success" || !data.result) {
                setOtpError("Invalid OTP. Please try again.");
                return;
            }

            window.localStorage.setItem("email_address", data.result.email_address);
            window.localStorage.setItem("phone_number", data.result.phone_number);
            window.localStorage.setItem("user_id", data.result.user_id);
            window.localStorage.setItem("signup", data.result.signup);
            if (data.result.signup === "user") {
                window.localStorage.setItem("business_id", data.result.business_id);
            }
            // Seller session for plan checkout and invoices
            saveSellerToken(data.result.seller_token);

            // Dispatch custom event to notify PlanContext to refresh
            window.dispatchEvent(new Event('userLoggedIn'));

            if (existingAccount) {
                // Signed in, as on the login page: no Store Ready popup or onboarding story
                window.sessionStorage.removeItem(STORE_READY_KEY);
                window.sessionStorage.removeItem(ONBOARDING_TO_PRODUCTS_KEY);
                toast.success("Login successful!");
                const next = await loginLandingPath(data.result.business_id, "/gift");
                setTimeout(() => { window.location.href = next; }, 1000);
                return;
            }
            toast.success('OTP verified, Sign-up Successful');

            // A new account sees the onboarding slider even if another one did in this browser
            window.localStorage.removeItem("hasSeenOnboarding");
            // Nor carries over a slider hand-off left by an earlier signup in this tab
            window.sessionStorage.removeItem(ONBOARDING_TO_PRODUCTS_KEY);
            const pendingPlanId = getPendingPlan();
            if (pendingPlanId) {
                // Plan picked on mytamilkart.com: straight to its checkout, as the Store
                // Ready popup's Buy Plan does: after payment, the slider, then the products
                window.sessionStorage.setItem(ONBOARDING_TO_PRODUCTS_KEY, "true");
            } else {
                // New store: the dashboard opens with the Store Ready popup (components/StoreReady.js)
                window.sessionStorage.setItem(STORE_READY_KEY, JSON.stringify({
                    store_url: data.result.store_url || "",
                }));
            }
            setTimeout(() => {
                window.location.href = pendingPlanId ? purchasePlanPath(pendingPlanId) : `/`;
            }, 1000);
        } catch (error) {
            console.error("Error during OTP verification or registration:", error);
            setOtpError(rateLimitMessage(error, t("auth.too_many"))
                || (error.response?.status ? "Invalid OTP. Please try again." : "Something went wrong. Please try again later."));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOtpChange = (value) => {
        setOtp(value);
        setOtpError("");
        if (value.length === OTP_LENGTH) verifyOTP(value);
    };

    const handleResendOTP = async (e) => {
        e.preventDefault();
        if (resendTimer > 0) return;
        try {
            const { data } = await (existingAccount ? requestLoginOtp() : requestOtp());
            if (data.status !== "success") throw new Error(data.message);
            toast.success('OTP has been resent');
            setOtp("");
            setResendTimer(RESEND_SECONDS);
        } catch (error) {
            console.error("Error resending OTP:", error);
            toast.error(rateLimitMessage(error, t("auth.too_many")) || "Failed to resend OTP. Please try again later.");
        }
    };

    let phoneHint = { text: "", cls: "" };
    if (phoneNumber.length === 10 && !isMobileNumber(phoneNumber)) phoneHint = { text: t("auth.phone_bad"), cls: "bad" };
    else if (phoneStatus === "checking") phoneHint = { text: t("auth.phone_checking"), cls: "" };
    else if (phoneStatus === "new") phoneHint = { text: t("auth.phone_new"), cls: "ok" };
    else if (phoneStatus === "registered") phoneHint = { text: t("auth.phone_registered"), cls: "bad" };
    else if (phoneStatus === "error") phoneHint = { text: t("auth.phone_check_failed"), cls: "bad" };
    else if (phoneStatus === "limited") phoneHint = { text: t("auth.too_many"), cls: "bad" };

    let urlHint = { text: "", cls: "" };
    if (phoneStatus === "registered") urlHint = { text: t("auth.store_url_registered"), cls: "" };
    else if (storeUrlLocked) urlHint = { text: t("auth.store_url_locked"), cls: "" };
    else if (customStoreUrl && customStoreUrl.length < 3) urlHint = { text: t("auth.url_short"), cls: "bad" };
    else if (storeNameEndsWithHyphen) urlHint = { text: t("auth.url_hyphen_end"), cls: "bad" };
    else if (customStoreUrl && checkingUrl) urlHint = { text: t("auth.url_checking"), cls: "" };
    else if (urlAvailability.status === "available") urlHint = { text: t("auth.url_ok"), cls: "ok" };
    else if (urlAvailability.status === "unavailable") urlHint = { text: t("auth.url_taken"), cls: "bad" };
    else if (urlAvailability.status === "error") urlHint = { text: t("auth.url_error"), cls: "bad" };

    return (
        <AuthLayout solo={!pickedPlan} aside={pickedPlan && <PlanSummary picked={pickedPlan} />}>
            {!showOtpSection ? (
                <form onSubmit={handleSubmit} noValidate>
                    <div className="fhead">
                        <h1>{t("auth.signup_title")} <em>{t("auth.signup_title_em")}</em></h1>
                    </div>
                    <div className="duo"><span>{t("auth.zero_commission")}</span><span>{t("auth.no_gst")}</span></div>

                    <div className="fld">
                        <label htmlFor="ph">{t("auth.phone")} <span className="req" aria-hidden="true">*</span></label>
                        <div className="inp">
                            <span>+91</span>
                            <input
                                id="ph"
                                inputMode="numeric"
                                maxLength={10}
                                autoComplete="tel-national"
                                placeholder={t("auth.phone_placeholder")}
                                aria-describedby="phH"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
                            />
                        </div>
                        <span className={`hint ${phoneHint.cls}`} id="phH" aria-live="polite">
                            {phoneHint.text}
                            {phoneCheckFailed && (
                                <> <button type="button" className="retry" onClick={() => setPhoneCheckTry(n => n + 1)}>{t("auth.try_again")}</button></>
                            )}
                        </span>
                    </div>

                    <label className="chk">
                        <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
                        <span>{t("auth.accept")} <Link to="/terms-condition">{t("auth.terms")}</Link></span>
                    </label>

                    <div className="storebox">
                        <label className="chk">
                            <input type="checkbox" checked={createWebStore} disabled={phoneStatus === "registered"} onChange={(e) => setCreateWebStore(e.target.checked)} />
                            <span>{t("auth.create_store")}</span>
                        </label>
                        {createWebStore && (
                            <div className="fld">
                                <label htmlFor="sn">{t("auth.store_url")} <span className="req" aria-hidden="true">*</span></label>
                                <div className="inp">
                                    <span className="pre">https://</span>
                                    <input
                                        id="sn"
                                        maxLength={30}
                                        autoComplete="off"
                                        placeholder={t("auth.store_placeholder")}
                                        aria-describedby="snH"
                                        disabled={storeUrlLocked}
                                        value={customStoreUrl}
                                        onChange={(e) => setCustomStoreUrl(typedStoreName(e.target.value))}
                                    />
                                    <span>.mytamilkart.com</span>
                                </div>
                                <span className={`hint ${urlHint.cls}`} id="snH">{urlHint.text}</span>
                                {urlAvailability.status === "unavailable" && urlSuggestions.length > 0 && (
                                    <div className="sugg">
                                        {t("auth.try")}
                                        {urlSuggestions.map(suggestion => (
                                            <button key={suggestion} type="button" onClick={() => setCustomStoreUrl(suggestion)}>{suggestion}</button>
                                        ))}
                                    </div>
                                )}
                                {customStoreUrl && !storeUrlLocked && !storeNameEndsWithHyphen && (
                                    <span className="preview" aria-live="polite">
                                        {t("auth.your_link")} <b>https://{customStoreUrl}.mytamilkart.com</b>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    <button className="go" type="submit" disabled={isSubmitting}>
                        {phoneStatus === "registered"
                            ? (isSubmitting ? t("auth.sending") : t("auth.sign_in"))
                            : (isSubmitting ? t("auth.creating") : t("auth.create_btn"))}
                    </button>
                    <p className="hint bad center" role="alert">{formError}</p>
                    <p className="signin">{t("auth.have_account")} <a href="/login">{t("auth.sign_in")}</a></p>
                </form>
            ) : (
                <form className="otpf" onSubmit={(e) => { e.preventDefault(); verifyOTP(otp); }} noValidate>
                    <h2>{t("auth.enter_otp")}<small>{t("auth.sent_to", { phone: spacedPhone(phoneNumber) })}</small></h2>
                    <OtpBoxes length={OTP_LENGTH} value={otp} onChange={handleOtpChange} />
                    <p className="hint bad center" role="alert">{otpError}</p>
                    <button className="go" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? t("auth.verifying") : t("auth.verify_create")}
                    </button>
                    <p className="signin">
                        <a href="#ph" onClick={(e) => { e.preventDefault(); setShowOtpSection(false); setExistingAccount(false); }}>{t("auth.change_number")}</a>
                        {" · "}
                        <button type="button" onClick={handleResendOTP} disabled={resendTimer > 0}>
                            {resendTimer > 0 ? t("auth.resend_in", { s: resendTimer }) : t("auth.resend")}
                        </button>
                    </p>
                </form>
            )}
        </AuthLayout>
    );
};

export default RegistrationForm;
